import { Color, Vec3 } from 'playcanvas';
import { Container } from '@playcanvas/pcui';
import { localize } from './localization';

import { Events } from '../events';

class EvidencePanel extends Container {
    constructor(events: Events, args = {}) {
        super({ ...args, id: 'evidence-panel', hidden: true });

        // Prevent wheel and pointer events from reaching the canvas behind the panel
        this.dom.addEventListener('wheel', (e) => e.stopPropagation(), { passive: false });
        this.dom.addEventListener('pointerdown', (e) => e.stopPropagation());

        // ── Header ──────────────────────────────────────────────────────────
        const header = document.createElement('div');
        header.id = 'evidence-panel-header';

        const title = document.createElement('span');
        title.id = 'evidence-panel-title';
        title.textContent = localize('evidence.evidence');

        const addBtn = document.createElement('button');
        addBtn.id = 'evidence-add-btn';
        addBtn.textContent = '+';
        addBtn.title = localize('evidence.add-new-evidence');
        addBtn.disabled = true;

        const closeBtn = document.createElement('button');
        closeBtn.id = 'evidence-panel-close';
        closeBtn.textContent = '×';

        header.appendChild(title);
        header.appendChild(addBtn);
        header.appendChild(closeBtn);
        this.dom.appendChild(header);

        // ── Scrollable list ─────────────────────────────────────────────────
        const listEl = document.createElement('div');
        listEl.id = 'evidence-list';
        this.dom.appendChild(listEl);

        // ── Create Form ─────────────────────────────────────────────────────
        const createForm = document.createElement('div');
        createForm.id = 'evidence-create-form';
        createForm.style.display = 'none';
        this.dom.appendChild(createForm);

        // Name group
        const nameGroup = document.createElement('div');
        nameGroup.className = 'ev-form-group';
        const nameLbl = document.createElement('label');
        nameLbl.className = 'ev-form-label';
        nameLbl.textContent = localize('evidence.name');
        const nameInput = document.createElement('input');
        nameInput.type = 'text';
        nameInput.className = 'ev-form-text-input';
        nameInput.placeholder = localize('evidence.name-box'); 
        nameGroup.appendChild(nameLbl);
        nameGroup.appendChild(nameInput);
        createForm.appendChild(nameGroup);

        // Position group
        const posGroup = document.createElement('div');
        posGroup.className = 'ev-form-group';
        const posLbl = document.createElement('label');
        posLbl.className = 'ev-form-label';
        posLbl.textContent = localize('evidence.position');
        const posRow = document.createElement('div');
        posRow.className = 'ev-coords-row';

        const makePosInput = (lbl: string) => {
            const wrap = document.createElement('div');
            wrap.style.display = 'contents';
            const l = document.createElement('span');
            l.className = 'ev-coord-label';
            l.textContent = lbl;
            const inp = document.createElement('input');
            inp.type = 'number';
            inp.className = 'ev-coord-input';
            inp.step = '0.001';
            inp.value = '0';
            posRow.appendChild(l);
            posRow.appendChild(inp);
            return inp;
        };

        const posX = makePosInput('X');
        const posY = makePosInput('Y');
        const posZ = makePosInput('Z');

        const useFocusBtn = document.createElement('button');
        useFocusBtn.className = 'ev-form-btn-secondary';
        useFocusBtn.textContent = localize('evidence.use-camera-focus');

        const gizmoHint = document.createElement('span');
        gizmoHint.className = 'ev-form-hint';
        gizmoHint.textContent = localize('evidence.use-camera-focus-extend');

        posGroup.appendChild(posLbl);
        posGroup.appendChild(posRow);
        posGroup.appendChild(useFocusBtn);
        posGroup.appendChild(gizmoHint);
        createForm.appendChild(posGroup);

        // Size group
        const sizeGroup = document.createElement('div');
        sizeGroup.className = 'ev-form-group';
        const sizeLbl = document.createElement('label');
        sizeLbl.className = 'ev-form-label';
        sizeLbl.textContent = localize('evidence.size');
        const sizeRow = document.createElement('div');
        sizeRow.className = 'ev-coords-row';

        const makeSizeInput = (lbl: string) => {
            const l = document.createElement('span');
            l.className = 'ev-coord-label';
            l.textContent = lbl;
            const inp = document.createElement('input');
            inp.type = 'number';
            inp.className = 'ev-coord-input';
            inp.step = '0.01';
            inp.min = '0.01';
            inp.value = '0.5';
            sizeRow.appendChild(l);
            sizeRow.appendChild(inp);
            return inp;
        };

        const sizeW = makeSizeInput('W');
        const sizeH = makeSizeInput('H');
        const sizeD = makeSizeInput('D');

        sizeGroup.appendChild(sizeLbl);
        sizeGroup.appendChild(sizeRow);
        createForm.appendChild(sizeGroup);

        // Photos group
        const photosGroup = document.createElement('div');
        photosGroup.className = 'ev-form-group';
        const photosLbl = document.createElement('label');
        photosLbl.className = 'ev-form-label';
        photosLbl.textContent = localize('evidence.photos');
        const pendingGrid = document.createElement('div');
        pendingGrid.className = 'ev-pending-grid';
        const addPhotosBtn = document.createElement('button');
        addPhotosBtn.className = 'ev-form-btn-secondary';
        addPhotosBtn.textContent = localize('evidence.add-photo-button');

        photosGroup.appendChild(photosLbl);
        photosGroup.appendChild(pendingGrid);
        photosGroup.appendChild(addPhotosBtn);
        createForm.appendChild(photosGroup);

        // Actions
        const actionsRow = document.createElement('div');
        actionsRow.className = 'ev-form-actions';
        const createBtn = document.createElement('button');
        createBtn.className = 'ev-form-btn-primary';
        createBtn.textContent = localize('evidence.create-evidence');
        const cancelBtn = document.createElement('button');
        cancelBtn.className = 'ev-form-btn-cancel';
        cancelBtn.textContent = localize('evidence.cancel');
        actionsRow.appendChild(createBtn);
        actionsRow.appendChild(cancelBtn);
        createForm.appendChild(actionsRow);

        // Stop propagation on all create-form inputs
        [nameInput, posX, posY, posZ, sizeW, sizeH, sizeD].forEach(inp => {
            inp.addEventListener('keydown', (e) => e.stopPropagation());
            inp.addEventListener('click', (e) => e.stopPropagation());
            inp.addEventListener('wheel', (e) => e.stopPropagation(), { passive: false });
        });

        // ── Lightbox — inline styles so it works regardless of SCSS load order
        const lightbox = document.createElement('div');
        Object.assign(lightbox.style, {
            display: 'none',
            position: 'fixed',
            inset: '0',
            background: 'rgba(0,0,0,0.88)',
            zIndex: '99999',
            alignItems: 'center',
            justifyContent: 'center',
        });

        const lbImg = document.createElement('img');
        Object.assign(lbImg.style, {
            maxWidth: '90vw',
            maxHeight: '90vh',
            objectFit: 'contain',
            borderRadius: '4px',
            boxShadow: '0 8px 48px rgba(0,0,0,0.8)',
            display: 'block',
        });

        const lbClose = document.createElement('button');
        Object.assign(lbClose.style, {
            position: 'absolute',
            top: '16px',
            right: '24px',
            fontSize: '32px',
            lineHeight: '1',
            background: 'none',
            border: 'none',
            color: '#fff',
            cursor: 'pointer',
            opacity: '0.75',
            padding: '0',
            fontFamily: 'inherit',
        });
        lbClose.textContent = '×';
        lbClose.addEventListener('mouseenter', () => { lbClose.style.opacity = '1'; });
        lbClose.addEventListener('mouseleave', () => { lbClose.style.opacity = '0.75'; });

        lightbox.appendChild(lbImg);
        lightbox.appendChild(lbClose);
        document.body.appendChild(lightbox);

        const openLightbox = (url: string) => {
            lbImg.src = url;
            lightbox.style.display = 'flex';
        };
        const closeLightbox = () => {
            lightbox.style.display = 'none';
            setTimeout(() => { lbImg.src = ''; }, 200);
        };
        lbClose.addEventListener('click', (e) => { e.stopPropagation(); closeLightbox(); });
        lightbox.addEventListener('click', (e) => { if (e.target === lightbox) closeLightbox(); });

        // ── Image URL cache ─────────────────────────────────────────────────
        let imageMap = new Map<string, string>();

        const refreshImages = async () => {
            imageMap.forEach(url => URL.revokeObjectURL(url));
            imageMap.clear();
            const tuples: [string, File][] = (await events.invoke('images.all')) ?? [];
            for (const [name, file] of tuples) {
                imageMap.set(name, URL.createObjectURL(file));
            }
        };

        // ── Temp zone state ─────────────────────────────────────────────────
        let tempZoneId: string | null = null;
        let pendingFiles: File[] = [];
        let pendingUrls: string[] = [];

        const getCurrentPosition = (): Vec3 => {
            return new Vec3(
                parseFloat(posX.value) || 0,
                parseFloat(posY.value) || 0,
                parseFloat(posZ.value) || 0
            );
        };

        const getCurrentHalfExtents = (): Vec3 => {
            return new Vec3(
                Math.max(0.01, parseFloat(sizeW.value) || 0.5),
                Math.max(0.01, parseFloat(sizeH.value) || 0.5),
                Math.max(0.01, parseFloat(sizeD.value) || 0.5)
            );
        };

        const updateTempZone = () => {
            if (!tempZoneId) return;
            events.fire('clickableZones.update', tempZoneId, {
                position: getCurrentPosition(),
                halfExtents: getCurrentHalfExtents(),
            });
        };

        [posX, posY, posZ].forEach(inp => {
            inp.addEventListener('input', () => {
                updateTempZone();
                events.fire('evidenceGizmo.setPosition', getCurrentPosition());
            });
        });
        [sizeW, sizeH, sizeD].forEach(inp => {
            inp.addEventListener('input', updateTempZone);
        });

        events.on('evidenceGizmo.moved', (pos: Vec3) => {
            posX.value = pos.x.toFixed(3);
            posY.value = pos.y.toFixed(3);
            posZ.value = pos.z.toFixed(3);
            updateTempZone();
        });

        // ── Create view open/close ──────────────────────────────────────────
        const showCreateForm = async () => {
            // Get camera focal point
            const fp: Vec3 = (await events.invoke('camera.getFocalPoint')) ?? new Vec3(0, 0, 0);
            posX.value = fp.x.toFixed(3);
            posY.value = fp.y.toFixed(3);
            posZ.value = fp.z.toFixed(3);
            sizeW.value = '0.5';
            sizeH.value = '0.5';
            sizeD.value = '0.5';
            nameInput.value = '';
            pendingFiles = [];
            pendingUrls.forEach(u => URL.revokeObjectURL(u));
            pendingUrls = [];
            pendingGrid.innerHTML = '';

            // Create temp preview zone
            const id = `__temp_${Date.now()}`;
            events.invoke('clickableZones.add', {
                id,
                position: fp.clone(),
                halfExtents: new Vec3(0.5, 0.5, 0.5),
                label: localize('evidence.new-evidence'),
                color: new Color(1.0, 0.5, 0.0),
                hoverColor: new Color(1.0, 0.7, 0.2),
            });
            tempZoneId = id;
            events.fire('evidenceGizmo.attach', fp.clone());

            listEl.style.display = 'none';
            createForm.style.display = 'flex';
        };

        const cancelCreate = () => {
            events.fire('evidenceGizmo.detach');
            if (tempZoneId) {
                events.fire('clickableZones.remove', tempZoneId);
                tempZoneId = null;
            }
            pendingFiles = [];
            pendingUrls.forEach(u => URL.revokeObjectURL(u));
            pendingUrls = [];
            pendingGrid.innerHTML = '';
            createForm.style.display = 'none';
            listEl.style.display = '';
        };

        addBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            showCreateForm();
        });

        cancelBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            cancelCreate();
        });

        useFocusBtn.addEventListener('click', async (e) => {
            e.stopPropagation();
            const fp: Vec3 = (await events.invoke('camera.getFocalPoint')) ?? new Vec3(0, 0, 0);
            posX.value = fp.x.toFixed(3);
            posY.value = fp.y.toFixed(3);
            posZ.value = fp.z.toFixed(3);
            updateTempZone();
            events.fire('evidenceGizmo.setPosition', fp.clone());
        });

        // Pending photo picker
        const pendingFileInput = document.createElement('input');
        pendingFileInput.type = 'file';
        pendingFileInput.accept = 'image/*';
        pendingFileInput.multiple = true;
        pendingFileInput.style.display = 'none';
        document.body.appendChild(pendingFileInput);

        addPhotosBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            pendingFileInput.value = '';
            pendingFileInput.click();
        });

        pendingFileInput.addEventListener('change', () => {
            const files = Array.from(pendingFileInput.files ?? []);
            for (const file of files) {
                pendingFiles.push(file);
                const url = URL.createObjectURL(file);
                pendingUrls.push(url);

                const wrap = document.createElement('div');
                wrap.className = 'evidence-thumb-wrap';

                const img = document.createElement('img');
                img.src = url;
                img.className = 'ev-pending-thumb';
                img.addEventListener('click', (e) => { e.stopPropagation(); openLightbox(url); });

                const removeBtn = document.createElement('button');
                removeBtn.className = 'evidence-thumb-remove';
                removeBtn.textContent = '×';
                removeBtn.title = localize('evidence.remove');
                removeBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const idx = pendingFiles.indexOf(file);
                    if (idx >= 0) {
                        pendingFiles.splice(idx, 1);
                        URL.revokeObjectURL(pendingUrls[idx]);
                        pendingUrls.splice(idx, 1);
                    }
                    wrap.remove();
                });

                wrap.appendChild(img);
                wrap.appendChild(removeBtn);
                pendingGrid.appendChild(wrap);
            }
        });

        // Create evidence
        createBtn.addEventListener('click', async (e) => {
            e.stopPropagation();

            const label = nameInput.value.trim() || 'Evidence';
            const position = getCurrentPosition();
            const halfExtents = getCurrentHalfExtents();

            events.fire('evidenceGizmo.detach');

            // Remove temp zone
            if (tempZoneId) {
                events.fire('clickableZones.remove', tempZoneId);
                tempZoneId = null;
            }

            // Load pending photos
            let imageNames: string[] = [];
            const filesToPersist = [...pendingFiles];
            if (pendingFiles.length > 0) {
                const tuples: [string, string][] = (await events.invoke('images.load', pendingFiles)) ?? [];
                imageNames = tuples.map(([name]) => name);
            }

            // Add permanent zone — always use a UUID so IDs are stable across sessions
            const newId = crypto.randomUUID ? crypto.randomUUID() : `ev_${Date.now()}_${Math.random().toString(36).slice(2)}`;
            const zoneId = (await events.invoke('clickableZones.add', {
                id: newId,
                label,
                position,
                halfExtents,
                supportingImages: imageNames,
            })) as string;

            // Persist zone and photos
            persistRuntimeZone(zoneId, label, position, halfExtents);
            if (imageNames.length > 0) {
                persistExtraImages(zoneId, imageNames);
                persistImageFiles(filesToPersist, imageNames);
            }

            // Clean up pending
            pendingFiles = [];
            pendingUrls.forEach(u => URL.revokeObjectURL(u));
            pendingUrls = [];
            pendingGrid.innerHTML = '';

            // Switch back to list view
            createForm.style.display = 'none';
            listEl.style.display = '';
            buildList();
        });

        // ── Persistence helpers ──────────────────────────────────────────────
        // Keys are scoped per blgs file so different scenes don't bleed into each other
        let currentFileKey: string | null = null; // null = no file loaded yet
        const runtimeZonesKey  = () => `blg_rzones_${currentFileKey}`;
        const extraImagesKey   = () => `blg_eimages_${currentFileKey}`;
        const removedImagesKey = () => `blg_rimages_${currentFileKey}`;
        const deletedZonesKey  = () => `blg_delzones_${currentFileKey}`;

        events.on('blg.loaded', async (hash: string) => {
            currentFileKey = hash;
            didRestore = false;
            addBtn.disabled = false;
            await restorePersistedData();
        });

        events.on('scene.clear', () => {
            currentFileKey = null;
            didRestore = false;
            addBtn.disabled = true;
            // Defer so clickable-zone.ts has a chance to clear its map first
            setTimeout(() => buildList(), 0);
        });

        const IDB_NAME  = 'blg_evidence_db';
        const IMG_STORE = 'images';

        const openIDB = (): Promise<IDBDatabase> => new Promise((res, rej) => {
            const req = indexedDB.open(IDB_NAME, 1);
            req.onupgradeneeded = () => {
                if (!req.result.objectStoreNames.contains(IMG_STORE)) {
                    req.result.createObjectStore(IMG_STORE, { keyPath: 'name' });
                }
            };
            req.onsuccess = () => res(req.result);
            req.onerror  = () => rej(req.error);
        });

        const idbPut = async (name: string, type: string, data: ArrayBuffer) => {
            const db = await openIDB();
            await new Promise<void>((res, rej) => {
                const tx = db.transaction(IMG_STORE, 'readwrite');
                tx.objectStore(IMG_STORE).put({ name, type, data });
                tx.oncomplete = () => res();
                tx.onerror    = () => rej(tx.error);
            });
            db.close();
        };

        const idbGetAll = async (): Promise<{ name: string; type: string; data: ArrayBuffer }[]> => {
            const db = await openIDB();
            const rows = await new Promise<any[]>((res, rej) => {
                const tx  = db.transaction(IMG_STORE, 'readonly');
                const req = tx.objectStore(IMG_STORE).getAll();
                req.onsuccess = () => res(req.result);
                req.onerror   = () => rej(req.error);
            });
            db.close();
            return rows;
        };

        const persistRuntimeZone = (id: string, label: string, position: Vec3, halfExtents: Vec3) => {
            const arr: any[] = JSON.parse(localStorage.getItem(runtimeZonesKey()) ?? '[]');
            const idx = arr.findIndex((z: any) => z.id === id);
            const entry = {
                id, label,
                position:    { x: position.x,    y: position.y,    z: position.z },
                halfExtents: { x: halfExtents.x,  y: halfExtents.y, z: halfExtents.z },
            };
            if (idx >= 0) arr[idx] = entry; else arr.push(entry);
            localStorage.setItem(runtimeZonesKey(), JSON.stringify(arr));
        };

        const persistExtraImages = (zoneId: string, imageNames: string[]) => {
            const map: Record<string, string[]> = JSON.parse(localStorage.getItem(extraImagesKey()) ?? '{}');
            const existing = map[zoneId] ?? [];
            for (const n of imageNames) { if (!existing.includes(n)) existing.push(n); }
            map[zoneId] = existing;
            localStorage.setItem(extraImagesKey(), JSON.stringify(map));
        };

        const removePersistedImageAssociation = (zoneId: string, imageName: string) => {
            // Remove from extra images map
            const extra: Record<string, string[]> = JSON.parse(localStorage.getItem(extraImagesKey()) ?? '{}');
            if (extra[zoneId]) {
                extra[zoneId] = extra[zoneId].filter(n => n !== imageName);
                localStorage.setItem(extraImagesKey(), JSON.stringify(extra));
            }
            // Track in removed set so blgs-native images don't resurface on restore
            const removed: Record<string, string[]> = JSON.parse(localStorage.getItem(removedImagesKey()) ?? '{}');
            const list = removed[zoneId] ?? [];
            if (!list.includes(imageName)) list.push(imageName);
            removed[zoneId] = list;
            localStorage.setItem(removedImagesKey(), JSON.stringify(removed));
        };

        const persistDeletedZone = (id: string) => {
            // Remove from runtime zones list (if it was a runtime zone)
            const arr: any[] = JSON.parse(localStorage.getItem(runtimeZonesKey()) ?? '[]');
            localStorage.setItem(runtimeZonesKey(), JSON.stringify(arr.filter((z: any) => z.id !== id)));
            // Track deletion so blgs-native zones are filtered on next restore
            const deleted: string[] = JSON.parse(localStorage.getItem(deletedZonesKey()) ?? '[]');
            if (!deleted.includes(id)) deleted.push(id);
            localStorage.setItem(deletedZonesKey(), JSON.stringify(deleted));
            // Clean up image associations so they can't leak to a future zone with the same id
            const extra: Record<string, string[]> = JSON.parse(localStorage.getItem(extraImagesKey()) ?? '{}');
            delete extra[id];
            localStorage.setItem(extraImagesKey(), JSON.stringify(extra));
        };

        const persistImageFiles = async (files: File[], names: string[]) => {
            for (let i = 0; i < files.length && i < names.length; i++) {
                try {
                    const data = await files[i].arrayBuffer();
                    await idbPut(names[i], files[i].type, data);
                } catch (e) {
                    console.warn('[Evidence] Failed to save image to IDB:', e);
                }
            }
        };

        let didRestore = false;
        const restorePersistedData = async () => {
            if (didRestore) return;
            if (!currentFileKey) return; // no file loaded yet — nothing to restore
            didRestore = true;

            // 1. Restore image files from IndexedDB
            try {
                const rows = await idbGetAll();
                if (rows.length > 0) {
                    const files = rows.map(r => new File([r.data], r.name, { type: r.type }));
                    await events.invoke('images.load', files);
                }
            } catch (e) {
                console.warn('[Evidence] Failed to restore images from IDB:', e);
            }

            // 2. Restore runtime zones not already in map
            const arr: any[] = JSON.parse(localStorage.getItem(runtimeZonesKey()) ?? '[]');
            const existingIds: string[] = (await events.invoke('clickableZones.list')) ?? [];
            const existingSet = new Set(existingIds);
            for (const z of arr) {
                if (!existingSet.has(z.id)) {
                    await events.invoke('clickableZones.add', {
                        id: z.id,
                        label: z.label,
                        position:    new Vec3(z.position.x,    z.position.y,    z.position.z),
                        halfExtents: new Vec3(z.halfExtents.x,  z.halfExtents.y, z.halfExtents.z),
                    });
                }
            }

            // 3. Apply all persisted image associations (covers both blgs and runtime zones)
            const extraMap: Record<string, string[]>   = JSON.parse(localStorage.getItem(extraImagesKey())   ?? '{}');
            const removedMap: Record<string, string[]> = JSON.parse(localStorage.getItem(removedImagesKey()) ?? '{}');
            for (const [zoneId, imageNames] of Object.entries(extraMap)) {
                const removed = new Set(removedMap[zoneId] ?? []);
                const filtered = imageNames.filter(n => !removed.has(n));
                if (filtered.length > 0) {
                    events.fire('clickableZones.addImages', { id: zoneId, imageNames: filtered });
                }
            }

            // 4. Remove zones the user explicitly deleted (handles blgs-native zones that reload from file)
            const deletedIds: string[] = JSON.parse(localStorage.getItem(deletedZonesKey()) ?? '[]');
            for (const id of deletedIds) {
                events.fire('clickableZones.remove', id);
            }
        };

        // ── Build list ───────────────────────────────────────────────────────
        const buildList = async (focusId?: string) => {
            await restorePersistedData();
            await refreshImages();
            listEl.innerHTML = '';

            const zones: {
                id: string;
                label: string;
                supportingImages: string[];
            }[] = (await events.invoke('clickableZones.listAll')) ?? [];

            // Filter out temp zones
            const visibleZones = zones.filter(z => !z.id.startsWith('__temp_'));

            if (visibleZones.length === 0) {
                const empty = document.createElement('div');
                empty.className = 'evidence-empty';
                empty.textContent = localize('evidence.no-evidence-found');
                listEl.appendChild(empty);
                return;
            }

            for (const zone of visibleZones) {
                const item = document.createElement('div');
                item.className = 'evidence-item';
                item.dataset.zoneId = zone.id;

                // ── item header row ──────────────────────────────────────
                const itemHeader = document.createElement('div');
                itemHeader.className = 'evidence-item-header';

                const storageKey = `blg_evidence_${zone.id}`;
                const savedName = localStorage.getItem(storageKey);
                const displayLabel = savedName ?? zone.label;

                if (savedName && savedName !== zone.label) {
                    events.fire('clickableZones.rename', { id: zone.id, label: savedName });
                }

                const nameInputEl = document.createElement('input');
                nameInputEl.type = 'text';
                nameInputEl.className = 'evidence-name-input';
                nameInputEl.value = displayLabel;

                nameInputEl.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    e.stopPropagation();
                });
                nameInputEl.addEventListener('blur', () => {
                    const trimmed = nameInputEl.value.trim();
                    if (!trimmed) { nameInputEl.value = displayLabel; return; }
                    localStorage.setItem(storageKey, trimmed);
                    events.fire('clickableZones.rename', { id: zone.id, label: trimmed });
                });
                nameInputEl.addEventListener('click', (e) => e.stopPropagation());
                nameInputEl.addEventListener('wheel', (e) => e.stopPropagation(), { passive: false });

                const focusBtn = document.createElement('button');
                focusBtn.className = 'evidence-focus-btn';
                focusBtn.textContent = localize('evidence.focus');
                focusBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    events.fire('clickableZones.focusOn', zone.id);
                });

                // "+ Photo" button
                const addPhotoBtn = document.createElement('button');
                addPhotoBtn.className = 'evidence-add-photos-btn';
                addPhotoBtn.textContent = localize('evidence.add-photo');
                addPhotoBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const fileInput = document.createElement('input');
                    fileInput.type = 'file';
                    fileInput.accept = 'image/*';
                    fileInput.multiple = true;
                    fileInput.style.display = 'none';
                    document.body.appendChild(fileInput);
                    fileInput.addEventListener('change', async () => {
                        const files = Array.from(fileInput.files ?? []);
                        if (files.length === 0) { fileInput.remove(); return; }
                        const tuples: [string, string][] = (await events.invoke('images.load', files)) ?? [];
                        const imageNames = tuples.map(([name]) => name);
                        events.fire('clickableZones.addImages', { id: zone.id, imageNames });
                        // Persist association and image files
                        persistExtraImages(zone.id, imageNames);
                        persistImageFiles(files, imageNames);
                        fileInput.remove();
                        buildList(zone.id);
                    });
                    fileInput.click();
                });

                itemHeader.appendChild(nameInputEl);

                // action buttons on a second row for breathing room
                const deleteBtn = document.createElement('button');
                deleteBtn.className = 'evidence-delete-btn';
                deleteBtn.textContent = '🗑';
                deleteBtn.title = localize('evidence.remove-evidence')
                deleteBtn.addEventListener('click', async (e) => {
                    e.stopPropagation();
                    const confirmed = await events.invoke('showPopup', {
                        type: 'yesno',
                        header: localize('evidence.remove-evidence'),
                        message: localize('evidence.removal-approve', {label0: zone.label}),
                    });
                    if (confirmed?.action !== 'yes') return;
                    events.fire('clickableZones.remove', zone.id);
                    persistDeletedZone(zone.id);
                    buildList();
                });

                const itemActions = document.createElement('div');
                itemActions.className = 'evidence-item-actions';
                itemActions.appendChild(focusBtn);
                itemActions.appendChild(addPhotoBtn);
                itemActions.appendChild(deleteBtn);

                item.appendChild(itemHeader);
                item.appendChild(itemActions);

                // ── images (collapsed by default) ────────────────────────
                const visibleImages = (zone.supportingImages ?? []).filter(n => imageMap.has(n));

                if (visibleImages.length > 0) {
                    const collapseBtn = document.createElement('button');
                    collapseBtn.className = 'evidence-collapse-btn';
                    collapseBtn.textContent = '▶';
                    collapseBtn.title = `${visibleImages.length} image${visibleImages.length !== 1 ? 's' : ''}`;
                    itemHeader.appendChild(collapseBtn);

                    const grid = document.createElement('div');
                    grid.className = 'evidence-images';
                    grid.style.display = 'none';

                    for (const imgName of visibleImages) {
                        const url = imageMap.get(imgName)!;

                        const wrap = document.createElement('div');
                        wrap.className = 'evidence-thumb-wrap';

                        const img = document.createElement('img');
                        img.src = url;
                        img.className = 'evidence-thumb';
                        img.title = imgName;
                        img.loading = 'lazy';
                        img.addEventListener('click', (e) => {
                            e.stopPropagation();
                            openLightbox(url);
                        });

                        const removeBtn = document.createElement('button');
                        removeBtn.className = 'evidence-thumb-remove';
                        removeBtn.textContent = '×';
                        removeBtn.title = 'Remove image';
                        removeBtn.addEventListener('click', (e) => {
                            e.stopPropagation();
                            events.fire('clickableZones.removeImage', { id: zone.id, imageName: imgName });
                            removePersistedImageAssociation(zone.id, imgName);
                            buildList(zone.id);
                        });

                        wrap.appendChild(img);
                        wrap.appendChild(removeBtn);
                        grid.appendChild(wrap);
                    }

                    let expanded = false;
                    collapseBtn.addEventListener('click', (e) => {
                        e.stopPropagation();
                        expanded = !expanded;
                        grid.style.display = expanded ? 'grid' : 'none';
                        collapseBtn.textContent = expanded ? '▼' : '▶';
                    });

                    item.appendChild(grid);
                }

                listEl.appendChild(item);
            }

            if (focusId) {
                const target = listEl.querySelector(`[data-zone-id="${focusId}"]`) as HTMLElement;
                target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        };

        // ── Visibility ───────────────────────────────────────────────────────
        let visible = false;

        const show = (focusId?: string) => {
            visible = true;
            this.hidden = false;
            events.fire('evidencePanel.visible', true);
            buildList(focusId);
        };

        const hide = () => {
            cancelCreate();
            visible = false;
            this.hidden = true;
            events.fire('evidencePanel.visible', false);
        };

        closeBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            hide();
        });

        events.on('evidencePanel.toggleVisible', () => (visible ? hide() : show()));
        events.on('evidencePanel.show', (id?: string) => show(id));
        events.on('evidencePanel.refresh', () => { if (!this.hidden) buildList(); });
        events.on('clickableZone.clicked', (id: string) => {
            if (!id.startsWith('__temp_')) show(id);
        });
    }
}

export { EvidencePanel };
